import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtH(mins) {
  const h = (mins || 0) / 60;
  return `${Math.round(h * 10) / 10}h`;
}

/**
 * ITHoursBankPanel — admin surface for setting monthly per-region IT
 * hours budgets. Live utilisation (used/remaining) is derived server-
 * side from the `time_entries` collection on read, so numbers stay
 * consistent with the ticket-level timewriting card.
 */
export function ITHoursBankPanel({ settings, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);

  const regionNames = (settings.regions || []).map((r) => (typeof r === "string" ? r : r.name)).filter(Boolean);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/hours-bank?year=${yearFilter}`);
      setRows(r.data.months || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearFilter, isAdmin]);

  if (!isAdmin) return null;

  return (
    <div className="hairline rounded-lg p-5 bg-white space-y-3" data-testid="it-hours-bank-panel">
      <div className="flex items-center gap-2">
        <Clock size={16} weight="duotone" className="text-[#4f4c90]" />
        <div className="font-heading font-semibold">IT hours bank</div>
      </div>
      <div className="text-xs text-slate-500">
        Monthly IT support budgets per region. Time booked on IT tickets is deducted from that month's bucket automatically.
        No rollover — set months in advance and amend individually as needed.
      </div>

      <BulkAddForm regionNames={regionNames} onSaved={load} saving={saving} setSaving={setSaving} />

      <div className="flex items-center gap-3 pt-2">
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">Year</div>
        <Select value={String(yearFilter)} onValueChange={(v) => setYearFilter(Number(v))}>
          <SelectTrigger className="bg-white border-slate-200 h-8 w-32" data-testid="hours-bank-year"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            {[-1, 0, 1, 2].map((delta) => {
              const y = new Date().getFullYear() + delta;
              return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic border border-dashed border-slate-200 rounded-md px-3 py-4 text-center">
          No hours set for {yearFilter}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hours-bank-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="text-left font-medium py-2 pr-3">Month</th>
                <th className="text-left font-medium py-2 px-3">Region</th>
                <th className="text-right font-medium py-2 px-3">Budget</th>
                <th className="text-right font-medium py-2 px-3">Used</th>
                <th className="text-right font-medium py-2 px-3">Remaining</th>
                <th className="text-left font-medium py-2 pl-3">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const pct = r.budget_minutes > 0 ? Math.min(100, Math.round((r.used_minutes / r.budget_minutes) * 100)) : 0;
                const overspent = r.remaining_minutes < 0;
                return (
                  <tr key={`${r.year}-${r.month}-${r.region}`} data-testid={`hours-bank-row-${r.year}-${r.month}-${r.region}`}>
                    <td className="py-2 pr-3 font-mono text-xs">{MONTHS[r.month]} {r.year}</td>
                    <td className="py-2 px-3">{r.region}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{fmtH(r.budget_minutes)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      <div>{fmtH(r.used_minutes)} <span className="text-slate-500">({pct}%)</span></div>
                    </td>
                    <td className={`py-2 px-3 text-right font-mono text-xs ${overspent ? "text-red-500" : "text-slate-700"}`}>{fmtH(r.remaining_minutes)}</td>
                    <td className="py-2 pl-3 text-[11px] text-slate-500 italic truncate max-w-xs">{r.note || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BulkAddForm({ regionNames, onSaved, saving, setSaving }) {
  const now = new Date();
  const [draft, setDraft] = useState({
    year: now.getFullYear(),
    start_month: now.getMonth() + 1,
    months: 6,
    region: "",
    hours: 40,
    note: "",
  });

  const submit = async () => {
    if (!draft.region) { toast.error("Pick a region"); return; }
    setSaving(true);
    try {
      await api.post("/admin/hours-bank/bulk", {
        year: Number(draft.year),
        start_month: Number(draft.start_month),
        months: Number(draft.months),
        region: draft.region,
        budget_minutes: Math.max(0, Math.round(Number(draft.hours) * 60)),
        note: draft.note.trim() || null,
      });
      toast.success(`Set ${draft.months} month${draft.months === 1 ? "" : "s"} of ${draft.hours}h for ${draft.region}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-2">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">Set budget</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Select value={draft.region} onValueChange={(v) => setDraft({ ...draft, region: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="bulk-region"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            {regionNames.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" min={0} value={draft.hours}
          onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
          placeholder="Hours" className="bg-white border-slate-200 h-9" data-testid="bulk-hours" />
        <Input type="number" min={2020} max={2100} value={draft.year}
          onChange={(e) => setDraft({ ...draft, year: e.target.value })}
          placeholder="Year" className="bg-white border-slate-200 h-9" data-testid="bulk-year" />
        <Select value={String(draft.start_month)} onValueChange={(v) => setDraft({ ...draft, start_month: v })}>
          <SelectTrigger className="bg-white border-slate-200 h-9" data-testid="bulk-start-month"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            {MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" min={1} max={36} value={draft.months}
          onChange={(e) => setDraft({ ...draft, months: e.target.value })}
          placeholder="# months" className="bg-white border-slate-200 h-9" data-testid="bulk-months" />
        <Button onClick={submit} disabled={saving} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1" data-testid="bulk-save">
          <Plus size={12} weight="bold" /> {saving ? "Saving…" : "Set budget"}
        </Button>
      </div>
      <Input value={draft.note}
        onChange={(e) => setDraft({ ...draft, note: e.target.value })}
        placeholder="Optional note (visible to admins)" className="bg-white border-slate-200 h-9" data-testid="bulk-note" />
      <div className="text-[10px] text-slate-500 italic">
        Filling an existing month overwrites its budget. Time already booked stays counted.
      </div>
    </div>
  );
}
